import axios, { type AxiosResponse } from 'axios'
import BaseService from '../BaseService'

const PREMIUM_API = import.meta.env.VITE_PREMIUM_API_URL

// ==================== Type Definitions ====================

/** Valid boost commission levels (subset of CommissionLevel) */
export type BoostCommissionLevel = 12 | 15 | 18 | 20 | 25

/** Smart boost interval (date range) */
export interface SmartBoostInterval {
  startDate: string
  endDate: string | null
}

/** Create boost request body */
export interface CreateBoostBody {
  boostCommissionLevel: BoostCommissionLevel
  intervals: SmartBoostInterval[]
}

/** Update boost request body */
export interface UpdateBoostBody {
  boostCommissionLevel: BoostCommissionLevel
  interval: SmartBoostInterval
}

/** A smart boost record */
export interface SmartBoost {
  id: string
  accommodationId: number
  partnerId: number
  startDate: string
  endDate: string
  boostCommissionLevel: number
  baseCommissionLevel: number
  createdBy: string | null
  actionSource: 'USER' | 'JOB'
  validUntil: string
  createdAt: string
  replacedBy: string | null
  deactivationReason: 'Expired' | 'Deleted' | 'Replaced' | 'Campaign Altered' | null
  isActive: boolean
}

/** Active boosts grouped by commission level */
export type ActiveBoostsResponse = Record<string, SmartBoost[]>

/** Request interfaces */
export interface AccommodationRequest {
  accommodationId: string
}

export interface AddBoostRequest {
  accommodationId: string
  body: CreateBoostBody
}

export interface UpdateBoostRequest {
  accommodationId: string
  boostId: string
  body: UpdateBoostBody
}

export interface RemoveBoostRequest {
  accommodationId: string
  boostId: string
}

export interface BoostHistoryRequest {
  accommodationId: string
  commission: string | number
}

// ==================== Service Class ====================

class SmartBoostService extends BaseService {
  constructor() {
    super({
      baseUrl: PREMIUM_API,
    })
  }

  getBoostLevels(): Promise<AxiosResponse<BoostCommissionLevel[]>> {
    return axios({
      url: `${this.baseUrl}/rateconnect/boost/level`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  addNewBoost({ accommodationId, body }: AddBoostRequest): Promise<AxiosResponse<SmartBoost[]>> {
    return axios({
      url: `${this.baseUrl}/rateconnect/${accommodationId}/boost`,
      method: 'POST',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getActiveBoosts({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<ActiveBoostsResponse>> {
    return axios({
      url: `${this.baseUrl}/rateconnect/${accommodationId}/boost/active`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getBoostsHistory({ accommodationId, commission }: BoostHistoryRequest): Promise<AxiosResponse<SmartBoost[]>> {
    const queryString = commission !== 'all' && commission !== '' ? `?commissionLevels=${commission}` : ''

    return axios({
      url: `${this.baseUrl}/rateconnect/${accommodationId}/boost/history${queryString}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  updateBoost({ accommodationId, boostId, body }: UpdateBoostRequest): Promise<AxiosResponse<SmartBoost>> {
    return axios({
      url: `${this.baseUrl}/rateconnect/${accommodationId}/boost/${boostId}`,
      method: 'PUT',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  removeBoost({ accommodationId, boostId }: RemoveBoostRequest): Promise<AxiosResponse<void>> {
    return axios({
      url: `${this.baseUrl}/rateconnect/${accommodationId}/boost/${boostId}`,
      method: 'DELETE',
      headers: this.studioHeadersWithAttribution(),
    })
  }
}

export default new SmartBoostService()
